/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { ColDef, ColumnState } from 'ag-grid-community';
import {
  LineCrossingType,
  lineCrossingHighlightDefault,
} from 'components/dashboard/VisualizationSettings/LocationSelectionObject';
import {
  AlertColorType,
  AlertNotificationLogSettingsObject,
  defaultAlertCategoriesFiltered,
} from 'jotai/alertNotificationLog';
import { defaultCategoryToIsFiltered } from 'jotai/audioTranscription/AudioTranscriptionObject';
import { AudioTranscriptionSettingsObject } from 'jotai/audioTranscription/AudioTranscriptionSettingsObject';
import { DriverPositionsSettingsObject } from 'jotai/driverPositions';
import { FastestLapsTableSettingsObject, fastestLapsTableColDefs } from 'jotai/fastestLapsTable';
import { LapTimesGraphSettingsObject } from 'jotai/lapTimesGraph';
import { LapTimesTableSettingsObject } from 'jotai/lapTimesTable';
import { MapViewSettingsObject } from 'jotai/mapView';
import { PitRoadTableColId, PitRoadTableSettingsObject } from 'jotai/pitRoad';
import { PitStrategyTableSettingsObject } from 'jotai/pitStrategy';
import {
  DriverPointsSettingsObject,
  DriverPointsTableColId,
  ErdpDriverPointsTableColId,
  ManufacturerPointsSettingsObject,
  OwnerPointsSettingsObject,
  ownerPointsTableColDefs,
} from 'jotai/points';
import { RaceOrderTableColId, RaceOrderTableSettingsObject } from 'jotai/raceOrderTable';
import { RaceProgressionSettingsObject } from 'jotai/raceProgression';
import { RankAnalysisReportSettingsObject } from 'jotai/rankAnalysis';
import { RunningOrderTableSettingsObject } from 'jotai/runningOrderTable';
import { TrackTemperatureSettingsObject, TrackTemperatureTableColId } from 'jotai/trackTemperatureTable';
import { VehicleGPSQualitySettingsObject } from 'jotai/vehicleGpsQuality/VehicleGPSQualitySettingsObject';
import { VisualizationType } from 'jotai/visualization';
import { WeatherMapSettingsObject } from 'jotai/weather';
import { WindyLayers, WindyWeatherSettingsObject } from 'jotai/windy';
import { AlertType, MapType, OriginType, RunType } from 'models/viewModels';
import { LocalVehicleSelections } from './LocalVehicleSelectionsObject';
import { LocationHighlightSelectionsObject } from './LocationHighlightSelectionsObject';
import { VisualizationSettingsObject } from './VisualizationSettingsObject';
import { LapDeltaTableColId, LapDeltaTableSettingsObject } from 'jotai/lapDelta';

const defaultLocalVehicleSelections: LocalVehicleSelections = {
  useLocalSelections: false,
  selectedGroup: 'none',
  selectedOems: [],
  selectedPerformances: [],
  selectedPlayoffDrivers: 'none',
  selectedPlayoffTopDrivers: 0,
  selectedLeader: 'leaderOnly',
  individualSelections: [],
};

// TODO: Use for all visualization types
const formatDefaultColumnStates = (colDef: ColDef[]): ColumnState[] =>
  colDef.map((item) => {
    return { colId: item.colId ? item.colId : '', sort: item.sort, hide: item.hide };
  });

export const getVisualizationSettingsDefault = (id: string, type: string, sessionType?: RunType) => {
  switch (type) {
    case VisualizationType.RaceOrderTable:
      const raceOrderSettings: RaceOrderTableSettingsObject = {
        id,
        columnStates: [
          { colId: RaceOrderTableColId.RunningPosition, sort: 'asc' },
          { colId: RaceOrderTableColId.DriverFullName, hide: true },
          { colId: RaceOrderTableColId.Group, hide: true },
          { colId: RaceOrderTableColId.PPLap, hide: true },
          { colId: RaceOrderTableColId.PPStrat, hide: true },
          { colId: RaceOrderTableColId.BestLap, hide: true },
        ],
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        dataFeedSelections: OriginType.MULTILOOP,
        locationHighlightSelections: {
          location: LineCrossingType.NONE,
          color: lineCrossingHighlightDefault,
        },
      };
      return raceOrderSettings;
    case VisualizationType.PitRoadSummaryTable:
      const pitRoadSettings: PitRoadTableSettingsObject = {
        id,
        columnStates: [
          {
            colId: PitRoadTableColId.DriverName,
            hide: true,
          },
          { colId: PitRoadTableColId.RearLeftTireChanged, hide: true },
          { colId: PitRoadTableColId.RearRightTireChanged, hide: true },
          { colId: PitRoadTableColId.FrontLeftTireChanged, hide: true },
          { colId: PitRoadTableColId.FrontRightTireChanged, hide: true },
          { colId: PitRoadTableColId.ProPitStopType, hide: true },
          { colId: PitRoadTableColId.Infractions, hide: true },
        ],
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        lineCrossingSelections: {},
        dataFeedSelections: OriginType.ERDP,
      };
      return pitRoadSettings;
    case VisualizationType.PitStrategyTable:
      const pitStrategySettings: PitStrategyTableSettingsObject = {
        id,
        columnStates: [],
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
      };
      return pitStrategySettings;
    case VisualizationType.TrackViewMap:
      const mapViewSettings: MapViewSettingsObject = {
        id,
        showLeaderIndicator: true,
        showLuckyDogIndicator: false,
        showOffLeadLapVehiclesIndicator: false,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        dataFeedSelections: OriginType.ERDP,
        mapViewTypeSelection: MapType.MAP,
        showLineCrossings: false,
      };
      return mapViewSettings;
    case VisualizationType.RunningOrderTable:
      const runningOrderTable: RunningOrderTableSettingsObject = {
        id,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        dataFeedSelections: OriginType.MULTILOOP,
      };
      return runningOrderTable;
    case VisualizationType.WindyWeather:
      const windyWeather: WindyWeatherSettingsObject = {
        id,
        zoom: 12,
        layer: WindyLayers.RADAR,
        pressure: true,
        marker: true,
        autoRefresh: true,
      };
      return windyWeather;
    case VisualizationType.RankAnalysisReport:
      const rankAnalysisReport: RankAnalysisReportSettingsObject = {
        id,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        dataFeedSelections: OriginType.MULTILOOP,
        slowTime: undefined,
        mediumTime: undefined,
        fastTime: undefined,
        sortType: 'vehicleNumber',
        slowThresholdColor: 'highlight-yellow-200',
        slowThresholdColorPallete: 'highlight',
        slowColorPallete: 'highlight',
        slowColor: 'highlight-yellow-400',
        mediumColor: 'highlight-green-400',
        mediumColorPallete: 'highlight',
        fastColor: 'highlight-green-600',
        fastColorPallete: 'highlight',
        isSlowThresholdColorEnabled: true,
        isSlowColorEnabled: false,
        isMediumColorEnabled: true,
        isFastColorEnabled: true,
        xLap: -1,
        practiceQualifyingGroupFilter: 'ALL',
      };
      return rankAnalysisReport;
    case VisualizationType.LapDeltaTable:
      const LapDeltaSettings: LapDeltaTableSettingsObject = {
        id,
        columnStates: [
          { colId: LapDeltaTableColId.Location, sort: 'asc' },
          { colId: LapDeltaTableColId.Driver },
          { colId: LapDeltaTableColId.Target },
          { colId: LapDeltaTableColId.Delta },
          { colId: LapDeltaTableColId.Total },
        ],
      };
      return LapDeltaSettings;
    case VisualizationType.LapTimesTable:
      const lapTimesTable: LapTimesTableSettingsObject = {
        id,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        columnStates: [],
        dataFeedSelections: OriginType.MULTILOOP,
        localVehicleSelectionsSortOrder: [],
        sortOrder: [],
        isAutoScroll: false,
      };
      return lapTimesTable;
    case VisualizationType.LapTimesGraph:
      const lapTimesGraphSettings: LapTimesGraphSettingsObject = {
        id,
        xMinimum: 1,
        lapsAutoIncrement: false,
        enableAutoScaling: true,
        timeofDayEnabled: false,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        dataFeedSelections: OriginType.MULTILOOP,
        isInlineSettingsShown: true,
        isVehicleLegendShown: true,
      };
      return lapTimesGraphSettings;
    case VisualizationType.TrackTemperature:
      const trackTemperatureSettings: TrackTemperatureSettingsObject = {
        id,
        isLive: true,
        showGraph: true,
        showHeatMap: true,
        showTable: true,
        columnStates: [{ colId: TrackTemperatureTableColId.CameraTakeTimestamp, sort: 'desc' }],
      };
      return trackTemperatureSettings;
    case VisualizationType.WeatherMap:
      const weatherMapSettings: WeatherMapSettingsObject = {
        id,
        radiusRings: false,
      };
      return weatherMapSettings;
    case VisualizationType.RaceProgression:
      const raceProgressionSettings: RaceProgressionSettingsObject = {
        id,
        items: {
          event: true,
          sessionType: true,
          timeCompletion: true,
          stageNum: sessionType === RunType.RACE,
          raceLaps: sessionType === RunType.RACE,
          raceLeft: sessionType === RunType.RACE,
          stageLaps: sessionType === RunType.RACE,
          stageLeft: sessionType === RunType.RACE,
          greenLaps: sessionType === RunType.RACE,
          yellowLaps: sessionType === RunType.RACE,
          flag: false,
        },
      };
      return raceProgressionSettings;
    case VisualizationType.FastestLapsTable:
      const fastestLapsTable: FastestLapsTableSettingsObject = {
        id,
        columnStates: formatDefaultColumnStates(fastestLapsTableColDefs),
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        dataFeedSelections: OriginType.MULTILOOP,
        topXLaps: 50,
        isConsecutive: false,
        timeThreshold: undefined,
      };
      return fastestLapsTable;
    case VisualizationType.AudioTranscription:
      const audioTranscriptionSettings: AudioTranscriptionSettingsObject = {
        id,
        roles: { Driver: true, Crew_Chief: true, Spotter: true, Unidentified: true },
        categories: defaultCategoryToIsFiltered,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        textFilter: '',
        displayTags: true,
      };
      return audioTranscriptionSettings;
    case VisualizationType.OwnerPoints:
      const ownerPointsSettings: OwnerPointsSettingsObject = {
        id,
        columnStates: formatDefaultColumnStates(ownerPointsTableColDefs),
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        dataFeedSelections: OriginType.LIVE,
      };
      return ownerPointsSettings;
    case VisualizationType.ManufacturerPoints:
      const manufacturerPointsSettings: ManufacturerPointsSettingsObject = {
        id,
        dataFeedSelections: OriginType.LIVE,
      };
      return manufacturerPointsSettings;
    case VisualizationType.DriverPoints:
      const driverPointsSettings: DriverPointsSettingsObject = {
        id,
        gapValue: '1',
        cutoffRank: 10,
        lastPlacePlayoffs: 10,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        columnStates: [
          { colId: DriverPointsTableColId.CurrentWins, hide: true },
          { colId: ErdpDriverPointsTableColId.FirstName, hide: true },
          { colId: ErdpDriverPointsTableColId.LastName, hide: true },
        ],
        dataFeedSelections: OriginType.LIVE,
      };
      return driverPointsSettings;
    case VisualizationType.DriverPositions:
      const driverPositionsSettings: DriverPositionsSettingsObject = {
        id,
        localVehicleSelections: {
          ...defaultLocalVehicleSelections,
        },
        isVehicleLegendShown: true,
        dataFeedSelections: OriginType.MULTILOOP,
      };
      return driverPositionsSettings;
    case VisualizationType.VehicleGPSQuality:
      const vehicleGPSQualitySettings: VehicleGPSQualitySettingsObject = {
        id,
        dataFeedSelections: OriginType.ERDP, 
      };
      return vehicleGPSQualitySettings;
    case VisualizationType.AlertNotificationLog:
      const alertNotificationSettings: AlertNotificationLogSettingsObject = {
        id,
        alertColorSelections: {
          [AlertType.FLAG]: AlertColorType.FLAGS,
          [AlertType.AUDIO]: AlertColorType.GLOBAL,
          [AlertType.PIT]: AlertColorType.GLOBAL,
          [AlertType.LUCKYDOG]: AlertColorType.GLOBAL,
          [AlertType.UNKNOWN]: AlertColorType.CUSTOM,
        },
        customAlertColors: {
          // Gray color
          [AlertType.FLAG]: '32',
          [AlertType.AUDIO]: '32',
          [AlertType.PIT]: '32',
          [AlertType.LUCKYDOG]: '32',
          [AlertType.UNKNOWN]: '32',
        },
        alertVehicleSelections: {
          [AlertType.FLAG]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.AUDIO]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.PIT]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.LUCKYDOG]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.UNKNOWN]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
        },
        categories: defaultAlertCategoriesFiltered,
        textFilter: '',
        audioKeywords: '',
        enableNotifications: false,
      };
      return alertNotificationSettings;
    default:
      return { id };
  }
};

export const upgrade = (settings: VisualizationSettingsObject, type: string): VisualizationSettingsObject => {
  switch (type) {
    case VisualizationType.TrackTemperature:
      if (!Object.hasOwn(settings, 'showHeatMap')) {
        (settings as TrackTemperatureSettingsObject).showHeatMap = true;
      }
      if (!Object.hasOwn(settings, 'showGraph')) {
        (settings as TrackTemperatureSettingsObject).showGraph = true;
      }
      if (!Object.hasOwn(settings, 'showTable')) {
        (settings as TrackTemperatureSettingsObject).showTable = true;
      }
      if (!Object.hasOwn(settings, 'isLive')) {
        (settings as TrackTemperatureSettingsObject).isLive = false;
      }
      break;
    case VisualizationType.WeatherMap:
      if (!settings) {
        settings = {
          radiusRings: false,
        } as WeatherMapSettingsObject;
      }
      if (!Object.hasOwn(settings, 'radiusRings')) {
        (settings as WeatherMapSettingsObject).radiusRings = false;
      }
      break;
    case VisualizationType.LapTimesGraph:
      if (!Object.hasOwn(settings, 'localVehicleSelections')) {
        (settings as LapTimesGraphSettingsObject).localVehicleSelections = {
          useLocalSelections: defaultLocalVehicleSelections.useLocalSelections,
          selectedGroup: defaultLocalVehicleSelections.selectedGroup,
          selectedOems: defaultLocalVehicleSelections.selectedOems,
          selectedPerformances: defaultLocalVehicleSelections.selectedPerformances,
          selectedPlayoffDrivers: defaultLocalVehicleSelections.selectedPlayoffDrivers,
          selectedPlayoffTopDrivers: defaultLocalVehicleSelections.selectedPlayoffTopDrivers,
          individualSelections: defaultLocalVehicleSelections.individualSelections,
          selectedLeader: defaultLocalVehicleSelections.selectedLeader,
        };
      } else if (
        Object.hasOwn(settings, 'localVehicleSelections') &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedPerformances')
      ) {
        (settings as LapTimesGraphSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesGraphSettingsObject).localVehicleSelections,
          selectedPerformances: defaultLocalVehicleSelections.selectedPerformances,
          selectedLeader: defaultLocalVehicleSelections.selectedLeader,
        };
      } else if (
        (settings as LapTimesGraphSettingsObject) &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedLeader')
      ) {
        (settings as LapTimesGraphSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesGraphSettingsObject).localVehicleSelections,
          selectedLeader: defaultLocalVehicleSelections.selectedLeader,
        };
      } else if (
        Object.hasOwn(settings, 'localVehicleSelections') &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedPlayoffDrivers')
      ) {
        (settings as LapTimesGraphSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesGraphSettingsObject).localVehicleSelections,
          selectedPlayoffDrivers: defaultLocalVehicleSelections.selectedPlayoffDrivers,
          selectedPlayoffTopDrivers: defaultLocalVehicleSelections.selectedPlayoffTopDrivers,
        };
      } else if ((settings as LapTimesGraphSettingsObject).localVehicleSelections.selectedGroup === 'performance') {
        (settings as LapTimesGraphSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesGraphSettingsObject).localVehicleSelections,
          selectedPerformances: (
            settings as LapTimesGraphSettingsObject
          ).localVehicleSelections.selectedPerformances.filter((item) => item !== 'luckyDog'),
        };
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as LapTimesGraphSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // For old workspaces that had SMT selected
      } else if (
        Object.hasOwn(settings, 'dataFeedSelections') &&
        (settings.dataFeedSelections === OriginType.SMT || settings.dataFeedSelections === OriginType.STRATEGY)
      ) {
        (settings as LapTimesGraphSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // TODO: Remove once RMonitor is back
      } else if (Object.hasOwn(settings, 'dataFeedSelections') && settings.dataFeedSelections === OriginType.RMONITOR) {
        (settings as LapTimesGraphSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
      }
      if (!Object.hasOwn(settings, 'isInlineSettingsShown')) {
        (settings as LapTimesGraphSettingsObject).isInlineSettingsShown = true;
      }
      if (!Object.hasOwn(settings, 'isVehicleLegendShown')) {
        (settings as LapTimesGraphSettingsObject).isVehicleLegendShown = true;
      }
      if (!Object.hasOwn(settings, 'enableAutoScaling')) {
        (settings as LapTimesGraphSettingsObject).enableAutoScaling = true;
      }
      break;
    case VisualizationType.LapTimesTable:
      if (!Object.hasOwn(settings, 'localVehicleSelections')) {
        (settings as LapTimesTableSettingsObject).localVehicleSelections = {
          ...defaultLocalVehicleSelections,
        };
      } else if (
        Object.hasOwn(settings, 'localVehicleSelections') &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedPerformances')
      ) {
        (settings as LapTimesTableSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesTableSettingsObject).localVehicleSelections,
          selectedPerformances: defaultLocalVehicleSelections.selectedPerformances,
          selectedLeader: defaultLocalVehicleSelections.selectedLeader,
        };
      } else if (
        (settings as LapTimesTableSettingsObject) &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedLeader')
      ) {
        (settings as LapTimesTableSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesTableSettingsObject).localVehicleSelections,
          selectedLeader: defaultLocalVehicleSelections.selectedLeader,
        };
      } else if (
        Object.hasOwn(settings, 'localVehicleSelections') &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedPlayoffDrivers')
      ) {
        (settings as LapTimesTableSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesTableSettingsObject).localVehicleSelections,
          selectedPlayoffDrivers: defaultLocalVehicleSelections.selectedPlayoffDrivers,
          selectedPlayoffTopDrivers: defaultLocalVehicleSelections.selectedPlayoffTopDrivers,
        };
      } else if ((settings as LapTimesTableSettingsObject).localVehicleSelections.selectedGroup === 'performance') {
        (settings as LapTimesTableSettingsObject).localVehicleSelections = {
          ...(settings as LapTimesTableSettingsObject).localVehicleSelections,
          selectedPerformances: (
            settings as LapTimesTableSettingsObject
          ).localVehicleSelections.selectedPerformances.filter((item) => item !== 'luckyDog'),
        };
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as LapTimesTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // For old workspaces that had SMT selected
      } else if (
        Object.hasOwn(settings, 'dataFeedSelections') &&
        (settings.dataFeedSelections === OriginType.SMT || settings.dataFeedSelections === OriginType.STRATEGY)
      ) {
        (settings as LapTimesTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // TODO: Remove once RMonitor is back
      } else if (Object.hasOwn(settings, 'dataFeedSelections') && settings.dataFeedSelections === OriginType.RMONITOR) {
        (settings as LapTimesTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
      }
      if (!Object.hasOwn(settings, 'columnStates')) {
        (settings as LapTimesTableSettingsObject).columnStates = [];
      }
      if (!Object.hasOwn(settings, 'localVehicleSelectionsSortOrder')) {
        (settings as LapTimesTableSettingsObject).localVehicleSelectionsSortOrder = [];
      }
      if (!Object.hasOwn(settings, 'sortOrder')) {
        (settings as LapTimesTableSettingsObject).sortOrder = [];
      }
      if (!Object.hasOwn(settings, 'isAutoScroll')) {
        (settings as LapTimesTableSettingsObject).isAutoScroll = false;
      }
      break;
    case VisualizationType.PitRoadSummaryTable:
      if (!Object.hasOwn(settings, 'localVehicleSelections')) {
        (settings as PitRoadTableSettingsObject).localVehicleSelections = {
          ...defaultLocalVehicleSelections,
        };
      } else if ((settings as PitRoadTableSettingsObject).localVehicleSelections.selectedGroup === 'performance') {
        (settings as PitRoadTableSettingsObject).localVehicleSelections = {
          ...(settings as PitRoadTableSettingsObject).localVehicleSelections,
          selectedPerformances: (
            settings as PitRoadTableSettingsObject
          ).localVehicleSelections.selectedPerformances.filter((item) => item !== 'luckyDog'),
        };
      } else if (
        Object.hasOwn(settings, 'localVehicleSelections') &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedPlayoffDrivers')
      ) {
        (settings as PitRoadTableSettingsObject).localVehicleSelections = {
          ...(settings as PitRoadTableSettingsObject).localVehicleSelections,
          selectedPlayoffDrivers: defaultLocalVehicleSelections.selectedPlayoffDrivers,
          selectedPlayoffTopDrivers: defaultLocalVehicleSelections.selectedPlayoffTopDrivers,
        };
      }
      if (!Object.hasOwn(settings, 'lineCrossingSelections')) {
        (settings as PitRoadTableSettingsObject).lineCrossingSelections = {};
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as PitRoadTableSettingsObject).dataFeedSelections = OriginType.ERDP;
      }
      break;
    case VisualizationType.RaceOrderTable:
      if (!Object.hasOwn(settings, 'localVehicleSelections')) {
        (settings as RaceOrderTableSettingsObject).localVehicleSelections = {
          ...defaultLocalVehicleSelections,
        };
      } else if ((settings as RaceOrderTableSettingsObject).localVehicleSelections.selectedGroup === 'performance') {
        (settings as RaceOrderTableSettingsObject).localVehicleSelections = {
          ...(settings as RaceOrderTableSettingsObject).localVehicleSelections,
          selectedPerformances: (
            settings as RaceOrderTableSettingsObject
          ).localVehicleSelections.selectedPerformances.filter((item) => item !== 'luckyDog'),
        };
      } else if (
        Object.hasOwn(settings, 'localVehicleSelections') &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedPlayoffDrivers')
      ) {
        (settings as RaceOrderTableSettingsObject).localVehicleSelections = {
          ...(settings as RaceOrderTableSettingsObject).localVehicleSelections,
          selectedPlayoffDrivers: defaultLocalVehicleSelections.selectedPlayoffDrivers,
          selectedPlayoffTopDrivers: defaultLocalVehicleSelections.selectedPlayoffTopDrivers,
        };
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as RaceOrderTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // For old workspaces that had SMT selected
      } else if (
        Object.hasOwn(settings, 'dataFeedSelections') &&
        (settings.dataFeedSelections === OriginType.SMT || settings.dataFeedSelections === OriginType.STRATEGY)
      ) {
        (settings as RaceOrderTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // TODO: Remove once RMonitor is back
      } else if (Object.hasOwn(settings, 'dataFeedSelections') && settings.dataFeedSelections === OriginType.RMONITOR) {
        (settings as LapTimesGraphSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
      }
      if (!Object.hasOwn(settings, 'locationHighlightSelections')) {
        (settings as LocationHighlightSelectionsObject).locationHighlightSelections = {
          location: LineCrossingType.NONE,
          color: lineCrossingHighlightDefault,
        };
      }
      break;
    case VisualizationType.RunningOrderTable:
      if (!Object.hasOwn(settings, 'localVehicleSelections')) {
        (settings as RunningOrderTableSettingsObject).localVehicleSelections = {
          ...defaultLocalVehicleSelections,
        };
      } else if ((settings as RunningOrderTableSettingsObject).localVehicleSelections.selectedGroup === 'performance') {
        (settings as RunningOrderTableSettingsObject).localVehicleSelections = {
          ...(settings as RunningOrderTableSettingsObject).localVehicleSelections,
          selectedPerformances: (
            settings as RunningOrderTableSettingsObject
          ).localVehicleSelections.selectedPerformances.filter((item) => item !== 'luckyDog'),
        };
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as RunningOrderTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // For old workspaces that had SMT selected
      } else if (
        Object.hasOwn(settings, 'dataFeedSelections') &&
        (settings.dataFeedSelections === OriginType.SMT || settings.dataFeedSelections === OriginType.STRATEGY)
      ) {
        (settings as RunningOrderTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
        // TODO: Remove once RMonitor is back
      } else if (Object.hasOwn(settings, 'dataFeedSelections') && settings.dataFeedSelections === OriginType.RMONITOR) {
        (settings as LapTimesGraphSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
      }
      break;
    case VisualizationType.TrackViewMap:
      if (!Object.hasOwn(settings, 'localVehicleSelections')) {
        (settings as MapViewSettingsObject).localVehicleSelections = {
          ...defaultLocalVehicleSelections,
        };
      } else if (
        Object.hasOwn(settings, 'localVehicleSelections') &&
        !Object.hasOwn(settings.localVehicleSelections, 'selectedPlayoffDrivers')
      ) {
        (settings as MapViewSettingsObject).localVehicleSelections = {
          ...(settings as MapViewSettingsObject).localVehicleSelections,
          selectedPlayoffDrivers: defaultLocalVehicleSelections.selectedPlayoffDrivers,
          selectedPlayoffTopDrivers: defaultLocalVehicleSelections.selectedPlayoffTopDrivers,
        };
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as MapViewSettingsObject).dataFeedSelections = OriginType.ERDP;
        // For old workspaces that had SMT selected
      } else if (
        Object.hasOwn(settings, 'dataFeedSelections') &&
        (settings.dataFeedSelections === OriginType.SMT || settings.dataFeedSelections === OriginType.STRATEGY)
      ) {
        (settings as MapViewSettingsObject).dataFeedSelections = OriginType.ERDP;
      }
      if (!Object.hasOwn(settings, 'mapViewTypeSelection')) {
        (settings as MapViewSettingsObject).mapViewTypeSelection = MapType.MAP;
      }
      if (!Object.hasOwn(settings, 'showLineCrossings')) {
        (settings as MapViewSettingsObject).showLineCrossings = false;
      }
      break;
    case VisualizationType.RankAnalysisReport:
      if (!Object.hasOwn(settings, 'slowThresholdColor')) {
        (settings as RankAnalysisReportSettingsObject).slowThresholdColor = 'highlight-yellow-200';
      }
      if (!Object.hasOwn(settings, 'slowColor')) {
        (settings as RankAnalysisReportSettingsObject).slowColor = 'highlight-yellow-400';
      }
      if (!Object.hasOwn(settings, 'mediumColor')) {
        (settings as RankAnalysisReportSettingsObject).mediumColor = 'highlight-green-400';
      }
      if (!Object.hasOwn(settings, 'fastColor')) {
        (settings as RankAnalysisReportSettingsObject).fastColor = 'highlight-green-600';
      }
      if (!Object.hasOwn(settings, 'isSlowThresholdColorEnabled')) {
        (settings as RankAnalysisReportSettingsObject).isSlowThresholdColorEnabled = true;
      }
      if (!Object.hasOwn(settings, 'isSlowColorEnabled')) {
        (settings as RankAnalysisReportSettingsObject).isSlowColorEnabled = false;
      }
      if (!Object.hasOwn(settings, 'isMediumColorEnabled')) {
        (settings as RankAnalysisReportSettingsObject).isMediumColorEnabled = true;
      }
      if (!Object.hasOwn(settings, 'isFastColorEnabled')) {
        (settings as RankAnalysisReportSettingsObject).isFastColorEnabled = true;
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as RankAnalysisReportSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
      }
      break;
    case VisualizationType.AudioTranscription:
      if (Object.hasOwn(settings, 'roles') && !settings.roles.hasOwnProperty('Unidentified')) {
        (settings as AudioTranscriptionSettingsObject).roles.Unidentified = true;
      }
      if (Object.hasOwn(settings, 'roles') && !settings.roles.hasOwnProperty('Crew_Chief')) {
        (settings as AudioTranscriptionSettingsObject).roles.Crew_Chief = true;
      }
      if (Object.hasOwn(settings, 'roles') && settings.roles.hasOwnProperty('CrewChief')) {
        delete settings.roles.CrewChief;
      }
      if (Object.hasOwn(settings, 'roles') && settings.roles.hasOwnProperty('Guest')) {
        delete settings.roles.Guest;
      }
      if (!Object.hasOwn(settings, 'textFilter')) {
        (settings as AudioTranscriptionSettingsObject).textFilter = '';
      }
      if (!Object.hasOwn(settings, 'categories')) {
        (settings as AudioTranscriptionSettingsObject).categories = defaultCategoryToIsFiltered;
      }
      if (!Object.hasOwn(settings, 'displayTags')) {
        (settings as AudioTranscriptionSettingsObject).displayTags = true;
      }
      break;
    case VisualizationType.DriverPoints:
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as DriverPointsSettingsObject).dataFeedSelections = OriginType.ERDP;
      }
      // Hide firstName by default if the setting doesn't exist yet
      if (
        Object.hasOwn(settings, 'columnStates') &&
        (settings as DriverPointsSettingsObject).columnStates.find(
          (columnState: ColumnState) => columnState.colId === ErdpDriverPointsTableColId.FirstName
        ) === undefined
      ) {
        (settings as DriverPointsSettingsObject).columnStates = [
          ...settings.columnStates,
          { colId: ErdpDriverPointsTableColId.FirstName, hide: true },
        ];
      }
      // Hide lastName by default if the setting doesn't exist yet
      if (
        Object.hasOwn(settings, 'columnStates') &&
        (settings as DriverPointsSettingsObject).columnStates.find(
          (columnState: ColumnState) => columnState.colId === ErdpDriverPointsTableColId.LastName
        ) === undefined
      ) {
        (settings as DriverPointsSettingsObject).columnStates = [
          ...settings.columnStates,
          { colId: ErdpDriverPointsTableColId.LastName, hide: true },
        ];
      }
      break;
    case VisualizationType.FastestLapsTable:
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as FastestLapsTableSettingsObject).dataFeedSelections = OriginType.MULTILOOP;
      }
      if (!Object.hasOwn(settings, 'isConsecutive')) {
        (settings as FastestLapsTableSettingsObject).isConsecutive = false;
      }

      break;
    case VisualizationType.OwnerPoints:
      if (!Object.hasOwn(settings, 'localVehicleSelections')) {
        (settings as OwnerPointsSettingsObject).localVehicleSelections = {
          ...defaultLocalVehicleSelections,
        };
      }
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as OwnerPointsSettingsObject).dataFeedSelections = OriginType.LIVE;
      }
      if (!Object.hasOwn(settings, 'columnStates')) {
        (settings as OwnerPointsSettingsObject).columnStates = formatDefaultColumnStates(ownerPointsTableColDefs);
      }
      break;
    case VisualizationType.ManufacturerPoints:
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as ManufacturerPointsSettingsObject).dataFeedSelections = OriginType.LIVE;
      }
      break;
    case VisualizationType.VehicleGPSQuality:
      if (!Object.hasOwn(settings, 'dataFeedSelections')) {
        (settings as VehicleGPSQualitySettingsObject).dataFeedSelections = OriginType.ERDP;
        // For old workspaces that had SMT selected
      } else if (
        Object.hasOwn(settings, 'dataFeedSelections') &&
        (settings.dataFeedSelections === OriginType.SMT || settings.dataFeedSelections === OriginType.STRATEGY)
      ) {
        (settings as VehicleGPSQualitySettingsObject).dataFeedSelections = OriginType.ERDP;
      }
      break;
    case VisualizationType.AlertNotificationLog:
      if (!Object.hasOwn(settings, 'alertColorSelections')) {
        (settings as AlertNotificationLogSettingsObject).alertColorSelections = {
          [AlertType.FLAG]: AlertColorType.FLAGS,
          [AlertType.AUDIO]: AlertColorType.CUSTOM,
          [AlertType.PIT]: AlertColorType.CUSTOM,
          [AlertType.LUCKYDOG]: AlertColorType.CUSTOM,
          [AlertType.UNKNOWN]: AlertColorType.CUSTOM,
        };
      }
      if (Object.hasOwn(settings, 'alertColorSelections')) {
        // if the alertColorSelections doesn't have the lucky dog key, add it
        const alertColorSelectionsKeys = Object.keys(settings.alertColorSelections);
        if (!alertColorSelectionsKeys.includes(AlertType.LUCKYDOG.toString())) {
          (settings as AlertNotificationLogSettingsObject).alertColorSelections[AlertType.LUCKYDOG] =
            AlertColorType.CUSTOM;
        }
      }
      if (!Object.hasOwn(settings, 'customAlertColors')) {
        (settings as AlertNotificationLogSettingsObject).customAlertColors = {
          // Gray color
          [AlertType.FLAG]: '32',
          [AlertType.AUDIO]: '32',
          [AlertType.PIT]: '32',
          [AlertType.LUCKYDOG]: '32',
          [AlertType.UNKNOWN]: '32',
        };
      }
      if (Object.hasOwn(settings, 'customAlertColors')) {
        // if the customAlertColors doesn't have the lucky dog key, add it
        const customAlertColorKeys = Object.keys(settings.customAlertColors);
        if (!customAlertColorKeys.includes(AlertType.LUCKYDOG.toString())) {
          (settings as AlertNotificationLogSettingsObject).customAlertColors[AlertType.LUCKYDOG] = '32';
        }
      }
      if (!Object.hasOwn(settings, 'alertVehicleSelections')) {
        (settings as AlertNotificationLogSettingsObject).alertVehicleSelections = {
          [AlertType.FLAG]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.AUDIO]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.PIT]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.LUCKYDOG]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
          [AlertType.UNKNOWN]: {
            localVehicleSelections: defaultLocalVehicleSelections,
            allVehicles: true,
          },
        };
      }
      if (!Object.hasOwn(settings, 'categories')) {
        (settings as AlertNotificationLogSettingsObject).categories = defaultAlertCategoriesFiltered;
      }
      if (!Object.hasOwn(settings, 'textFilter')) {
        (settings as AlertNotificationLogSettingsObject).textFilter = '';
      }
      if (!Object.hasOwn(settings, 'audioKeywords')) {
        (settings as AlertNotificationLogSettingsObject).audioKeywords = '';
      }
      if (!Object.hasOwn(settings, 'enableNotifications')) {
        (settings as AlertNotificationLogSettingsObject).enableNotifications = false;
      }
      break;
    default:
      break;
  }
  // Individual Selections change to an array of vehicle numbers as vehicle's now have an assigned default color.
  if (
    Object.hasOwn(settings, 'localVehicleSelections') &&
    !Array.isArray(settings.localVehicleSelections.individualSelections)
  ) {
    settings.localVehicleSelections.individualSelections = Object.keys(
      settings.localVehicleSelections.individualSelections
    ).map((vehicleNumber) => vehicleNumber);
  }

  return settings;
};
